import { APIError } from '../errors/APIError';
import { useAPIMutation, useAPIQuery } from '../useAPIProvider';
import { AddressRequest } from '../contracts/address';

export const useAddress = (mspId?: string, customerId?: string, id?: string) => {
  return useAPIQuery(['getAddress', mspId, customerId, id], async client => {
    if (!mspId || !id) return null;
    
    if (customerId) {
      const response = await client.address.getCustomerAddress({ params: { mspId, customerId, id } });
      
      if (response.status === 200) {
        return response.body;
      }

      // Fallback to Partner Address if Customer Address isn't found (e.g. Guest Checkout)
      const partnerResponse = await client.address.getPartnerAddress({ params: { mspId, id } });
      if (partnerResponse.status === 200) {
        return partnerResponse.body;
      }

      // If the address was truly not found in the database, the backend converts Result.Failure to 400 Bad Request
      if (response.status === 400 || partnerResponse.status === 400) {
        return null;
      }

      throw APIError.fromResponse(response);
    } else {
      const response = await client.address.getPartnerAddress({ params: { mspId, id } });
      if (response.status === 200) return response.body;
      
      if (response.status === 400) return null;
      
      throw APIError.fromResponse(response);
    }
  });
};

export const useGetAddresses = (mspId?: string, customerId?: string) => {
  return useAPIQuery(['getAddresses', mspId, customerId], async client => {
    if (!mspId) return [];

    if (customerId) {
      const response = await client.address.getCustomerAddresses({ params: { mspId, customerId } });
      if (response.status !== 200) throw APIError.fromResponse(response);
      return response.body;
    } else {
      const response = await client.address.getPartnerAddresses({ params: { mspId } });
      if (response.status !== 200) throw APIError.fromResponse(response);
      return response.body;
    }
  });
};

export const useCreateAddress = () => {
  return useAPIMutation((client, query) => {
    return async ({ mspId, customerId, ...payload }: AddressRequest & { mspId: string; customerId?: string }) => {
      if (customerId) {
        const response = await client.address.createCustomerAddress({
          params: { mspId, customerId },
          body: payload
        });
        if (response.status !== 200) throw APIError.fromResponse(response);
        await query.invalidateQueries(['getAddresses', mspId, customerId]);
        return response.body;
      } else {
        const response = await client.address.createPartnerAddress({
          params: { mspId },
          body: payload
        });
        if (response.status !== 200) throw APIError.fromResponse(response);
        await query.invalidateQueries(['getAddresses', mspId, undefined]);
        return response.body;
      }
    };
  });
};

export const useUpdateAddress = () => {
  return useAPIMutation((client, query) => {
    return async ({ id, mspId, customerId, ...payload }: AddressRequest & { id: string; mspId: string; customerId?: string }) => {
      if (customerId) {
        const response = await client.address.updateCustomerAddress({
          params: { mspId, customerId, id },
          body: payload
        });

        if (response.status === 200) {
          await query.invalidateQueries(['getAddress', mspId, customerId, id]);
          await query.invalidateQueries(['getAddresses', mspId, customerId]);
          return response.body;
        }

        // Fallback to Partner Address if Customer Address isn't found (e.g. Guest Checkout)
        const partnerResponse = await client.address.updatePartnerAddress({
          params: { mspId, id },
          body: payload
        });

        if (partnerResponse.status === 200) {
          await query.invalidateQueries(['getAddress', mspId, undefined, id]);
          await query.invalidateQueries(['getAddresses', mspId, undefined]);
          return partnerResponse.body;
        }

        throw APIError.fromResponse(response);
      } else {
        const response = await client.address.updatePartnerAddress({
          params: { mspId, id },
          body: payload
        });
        if (response.status !== 200) throw APIError.fromResponse(response);
        await query.invalidateQueries(['getAddress', mspId, undefined, id]);
        await query.invalidateQueries(['getAddresses', mspId, undefined]);
        return response.body;
      }
    };
  });
};

/**
 * LEGACY COMPATIBILITY HOOK for components explicitly using the new customer-specific name.
 */
export const useCreateCustomerAddress = () => {
  const createAddress = useCreateAddress();
  return {
    ...createAddress,
    mutateAsync: async (payload: AddressRequest & { mspId: string; customerId: string }) => {
      return createAddress.mutateAsync(payload);
    }
  };
};

export const useDeleteAddress = () => {
  return useAPIMutation((client, query) => {
    return async ({ id, mspId, customerId }: { id: string; mspId: string; customerId?: string }) => {
      if (customerId) {
        const response = await client.address.deleteCustomerAddress({
          params: { mspId, customerId, id },
          body: undefined
        });

        if (response.status === 204 || response.status === 200) {
          await query.invalidateQueries(['getAddress', mspId, customerId, id]);
          await query.invalidateQueries(['getAddresses', mspId, customerId]);
          return;
        }

        // Only fallback to Partner Address if Customer Address isn't found or accessible
        if (response.status === 404 || response.status === 403 || response.status === 400) {
          const partnerResponse = await client.address.deletePartnerAddress({
            params: { mspId, id },
            body: undefined
          });

          if (partnerResponse.status === 204 || partnerResponse.status === 200) {
            await query.invalidateQueries(['getAddress', mspId, undefined, id]);
            await query.invalidateQueries(['getAddresses', mspId, undefined]);
            return;
          }

          throw APIError.fromResponse(partnerResponse);
        }

        throw APIError.fromResponse(response);
      } else {
        const response = await client.address.deletePartnerAddress({
          params: { mspId, id },
          body: undefined
        });
        if (response.status !== 204 && response.status !== 200) throw APIError.fromResponse(response);
        await query.invalidateQueries(['getAddress', mspId, undefined, id]);
        await query.invalidateQueries(['getAddresses', mspId, undefined]);
        return;
      }
    };
  });
};
